// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// One symbol on an API page: a function, a method, or a class.
//
// Art direction, so entries stay consistent as the reference grows:
//
//   THE NAME IS THE SKIM LINE. A module can hold a hundred functions, and the
//   reader is running an eye down the left edge looking for one of them. So
//   the name is the only thing at full weight; the signature - longer, and
//   what you read once you have stopped - sits under it a size down.
//
//   RECEDE, DO NOT HIGHLIGHT. Meaning comes from what is pushed back. Defaults
//   and the return arrow drop to the lightest ink so the parameter names stay
//   legible through them. Tinting each part of a signature would give a
//   hundred lines of confetti and no hierarchy at all.
//
//   ONE CHIP, ONE HAZARD, ONE ACCENT. `optional` is the chip, because it is
//   the thing riggers scan for. `deprecated` is the hazard, and the only place
//   a warning colour appears. Brand blue means a link and nothing else - not
//   headings, not types, not names. Everything else is ink.
//
// Empty sections are omitted rather than rendered as "Parameters: None", and
// no name carries its own module prefix: the page header already says where
// you are, and repeating `core.maya.psd.` a hundred times is noise.
// ==========================================================================

import React, { useCallback, useMemo, useState } from 'react';
import { ApiClass, ApiDoc, ApiFunction, ApiParam } from './types';
import { buildSnippet, parseSignature } from './signature';
import { hasCode, parseBlocks } from './rst';
import { Resolver, RstBlocks, RstText } from './rst.component';

/** `int, optional` carries two facts. The chip is worth more than the word. */
const splitOptional = (type: string): { type: string; optional: boolean } => {
  const match = type.match(/^(.*?),?\s*optional\s*$/i);
  return match
    ? { type: match[1].trim(), optional: true }
    : { type: type.trim(), optional: false };
};

const hasBody = (doc: ApiDoc): boolean =>
  doc.params.length > 0 || Boolean(doc.returns) || doc.raises.length > 0;

// --------------------------------------------------------------- signature --

const Signature: React.FC<{ signature: string }> = ({ signature }) => {
  const { parts, tail } = parseSignature(signature);

  return (
    // Hanging indent, so a wrapped signature's continuation lines sit under
    // the first parameter rather than under the opening bracket. No
    // overflow-x: a scroller here would hide the return type off the edge,
    // which is the half people came for.
    <div className="mt-[6px] font-mono text-[12.5px] leading-[1.7] text-content-secondary pl-[2.5ch] -indent-[2.5ch] break-words">
      (
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {/* Each parameter is atomic, so lines break between them. */}
          <span className="whitespace-nowrap">
            {part.text}
            {part.fallback && (
              <span className="text-content-muted-light">={part.fallback}</span>
            )}
            {index < parts.length - 1 && ','}
          </span>{' '}
        </React.Fragment>
      ))}
      ){tail && <span className="text-content-muted-light"> {tail}</span>}
    </div>
  );
};

// ------------------------------------------------------------------- chrome --

const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-mono text-[10.5px] leading-none px-[6px] py-[3px] rounded-[4px] bg-surface-card text-content-muted-alt">
    {children}
  </span>
);

const CopyButton: React.FC<{ value: string; label: string }> = ({
  value,
  label
}) => {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  }, [value]);

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      className="shrink-0 font-body text-[11.5px] px-[8px] py-[3px] rounded-[5px] border border-border-dark/[0.14] text-content-muted-alt hover:text-blue hover:border-blue/[0.4] transition-colors"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/**
 * The name, with its anchor.
 *
 * The id is the bare symbol name: stable between releases, and guessable,
 * which matters when somebody types a URL from memory or pastes one into a
 * chat. The `#` appears on hover so a hundred of them do not fleck the page.
 */
const SymbolName: React.FC<{
  id: string;
  name: string;
  size: string;
  children?: React.ReactNode;
}> = ({ id, name, size, children }) => (
  // scroll-mt clears both the site header and the sticky module strip; without
  // it an anchored symbol lands underneath them.
  <div id={id} className="group flex items-baseline gap-[10px] scroll-mt-[124px]">
    <h4 className={`font-mono ${size} font-semibold text-content-primary`}>
      {name}
    </h4>
    <a
      href={`#${id}`}
      aria-label={`Link to ${name}`}
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-blue text-[13px] transition-opacity"
    >
      #
    </a>
    {children}
  </div>
);

// ------------------------------------------------------------- doc sections --

const ParamRows: React.FC<{ params: ApiParam[]; resolve?: Resolver }> = ({
  params,
  resolve
}) => (
  // A definition list on a grid rather than a table: the description column
  // does the wrapping, and the pair collapses to stacked on a narrow window
  // instead of forcing a sideways scroll.
  <dl className="mt-[14px] grid grid-cols-1 sm:grid-cols-[minmax(140px,200px)_1fr] gap-x-[18px] gap-y-[10px]">
    {params.map((param) => {
      const { type, optional } = splitOptional(param.type);
      return (
        <React.Fragment key={param.name}>
          <dt className="flex flex-wrap items-baseline gap-x-[7px] gap-y-[3px]">
            <span className="font-mono text-[12.5px] text-content-primary">
              {param.name}
            </span>
            {type && (
              <span className="font-mono text-[11.5px] text-content-muted-alt">
                {type}
              </span>
            )}
            {optional && (
              <span className="font-body text-[10.5px] leading-none px-[5px] py-[2px] rounded-[4px] bg-surface-section text-content-muted-alt">
                optional
              </span>
            )}
          </dt>
          <dd className="text-[13.5px] leading-[1.65] text-content-secondary m-0 sm:pt-[1px]">
            <RstText text={param.doc} resolve={resolve} />
          </dd>
        </React.Fragment>
      );
    })}
  </dl>
);

const DocBody: React.FC<{ doc: ApiDoc; resolve?: Resolver }> = ({
  doc,
  resolve
}) => (
  <>
    {doc.params.length > 0 && <ParamRows params={doc.params} resolve={resolve} />}

    {doc.returns && (
      <dl className="mt-[14px] grid grid-cols-1 sm:grid-cols-[minmax(140px,200px)_1fr] gap-x-[18px] gap-y-[10px]">
        <dt className="flex flex-wrap items-baseline gap-x-[7px]">
          <span className="font-body text-[12.5px] font-semibold text-content-primary">
            Returns
          </span>
          {doc.returns.type && (
            <span className="font-mono text-[11.5px] text-content-muted-alt">
              {doc.returns.type}
            </span>
          )}
        </dt>
        <dd className="text-[13.5px] leading-[1.65] text-content-secondary m-0 sm:pt-[1px]">
          <RstText text={doc.returns.doc} resolve={resolve} />
        </dd>
      </dl>
    )}

    {doc.raises.length > 0 && (
      <dl className="mt-[14px] grid grid-cols-1 sm:grid-cols-[minmax(140px,200px)_1fr] gap-x-[18px] gap-y-[10px]">
        {doc.raises.map((raise, index) => (
          <React.Fragment key={index}>
            <dt className="flex flex-wrap items-baseline gap-x-[7px]">
              <span className="font-body text-[12.5px] font-semibold text-content-primary">
                Raises
              </span>
              <span className="font-mono text-[11.5px] text-content-muted-alt">
                {raise.type}
              </span>
            </dt>
            <dd className="text-[13.5px] leading-[1.65] text-content-secondary m-0 sm:pt-[1px]">
              <RstText text={raise.doc} resolve={resolve} />
            </dd>
          </React.Fragment>
        ))}
      </dl>
    )}
  </>
);

// -------------------------------------------------------------------- entry --

/**
 * A function or a method.
 *
 * Name, signature and summary are always visible; parameters sit behind a
 * disclosure that names what is inside it, because a module of a hundred
 * functions expanded in full is sixty screens of scrolling and the arity and
 * return type on the disclosure row are often the whole answer.
 */
export const ApiEntry: React.FC<{
  entry: ApiFunction;
  module: string;
  className?: string;
  open: boolean;
  resolve?: Resolver;
}> = ({ entry, module, className, open, resolve }) => {
  const [expanded, setExpanded] = useState(open);

  // Kept in step when the page's Expand all / Collapse all is used, while
  // still allowing one entry to be toggled on its own afterwards.
  React.useEffect(() => setExpanded(open), [open]);

  const id = className ? `${className}.${entry.name}` : entry.name;
  const deprecated = entry.decorators.some((decorator) =>
    decorator.toLowerCase().includes('deprecated')
  );
  const blocks = useMemo(() => parseBlocks(entry.doc.summary), [entry.doc.summary]);
  // Only the opening paragraph stays above the fold. Everything the markup
  // introduces - a note, a code sample, a list - is multi-line, and a hundred
  // entries have to stay a hundred scannable names.
  const lead = blocks.slice(0, 1).filter((block) => block.kind === 'para');
  const rest = blocks.slice(lead.length);

  const detail = [
    entry.doc.params.length > 0 &&
      `${entry.doc.params.length} parameter${entry.doc.params.length === 1 ? '' : 's'}`,
    entry.doc.returns?.type && `returns ${entry.doc.returns.type}`,
    // Worth saying on the closed row: an example is the thing people open one
    // of these for.
    hasCode(rest) && 'example'
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className={`border-t border-border-dark/[0.08] pt-[22px] pb-[26px] ${
        deprecated ? 'border-l-[3px] border-l-warning-accent pl-[16px]' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-[14px]">
        <div className="min-w-0">
          <SymbolName
            id={id}
            name={entry.name}
            size={className ? 'text-[14px]' : 'text-[15px]'}
          >
            {entry.async && <Tag>async</Tag>}
            {entry.decorators
              .filter((decorator) => !decorator.toLowerCase().includes('deprecated'))
              .map((decorator) => (
                <Tag key={decorator}>{decorator}</Tag>
              ))}
            {deprecated && (
              <span className="font-mono text-[10.5px] leading-none px-[6px] py-[3px] rounded-[4px] bg-warning border border-warning-border text-warning-text">
                deprecated
              </span>
            )}
          </SymbolName>
          <Signature signature={entry.signature} />
        </div>
        <CopyButton
          value={buildSnippet(module, entry.name, entry.signature, className)}
          label={`Copy an import and call for ${entry.name}`}
        />
      </div>

      {lead.length > 0 && (
        <div className="mt-[10px]">
          <RstBlocks blocks={lead} resolve={resolve} tight />
        </div>
      )}

      {(hasBody(entry.doc) || rest.length > 0) && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            className="mt-[12px] flex items-center gap-[7px] text-[12.5px] text-content-muted-alt hover:text-blue transition-colors"
          >
            <span
              aria-hidden="true"
              className={`inline-block transition-transform ${expanded ? 'rotate-90' : ''}`}
            >
              ›
            </span>
            {detail || 'Details'}
          </button>
          {expanded && (
            <>
              {rest.length > 0 && <RstBlocks blocks={rest} resolve={resolve} />}
              <DocBody doc={entry.doc} resolve={resolve} />
            </>
          )}
        </>
      )}
    </div>
  );
};

/**
 * A class, with its methods behind a rail.
 *
 * The rail is the only reliable way to know you are still inside a class after
 * forty methods have scrolled past; indentation alone stops reading as
 * containment once the class heading is off screen.
 */
export const ApiClassBlock: React.FC<{
  entry: ApiClass;
  module: string;
  open: boolean;
  resolve?: Resolver;
}> = ({ entry, module, open, resolve }) => (
  <section className="mt-[44px]">
    <SymbolName id={entry.name} name={entry.name} size="text-[17px]">
      <Tag>class</Tag>
    </SymbolName>

    {entry.bases.length > 0 && (
      <p className="mt-[6px] font-mono text-[12px] text-content-muted-alt">
        {entry.bases.join(', ')}
      </p>
    )}

    {entry.doc.summary && (
      <div className="mt-[10px]">
        <RstBlocks blocks={parseBlocks(entry.doc.summary)} resolve={resolve} tight />
      </div>
    )}

    {entry.methods.length > 0 && (
      <div className="mt-[18px] border-l-2 border-border-dark/[0.10] pl-[20px]">
        {entry.methods.map((method) => (
          <ApiEntry
            key={method.name}
            entry={method}
            module={module}
            className={entry.name}
            open={open}
            resolve={resolve}
          />
        ))}
      </div>
    )}
  </section>
);
